// Sprite atlas: loads the WinBolo art sheets at startup, exposes draw helpers
// for tiles, tanks, boats, pillboxes, bases, shells, LGMs, and explosions.
//
// Art credit: WinBolo by John Morrison, GPL-2.0. See LICENSE-WINBOLO and the
// README "Credits" section. Sprites are 16×16 in the original sheets; we
// scale up to TILE_PX in the renderer.
//
// Atlas coordinates below are ported from WinBolo's `gui/tiles.h` so that the
// canonical sprite layout is the single source of truth. Where WinBolo
// distinguishes between "good" / "evil" / "neutral" affiliation we expose the
// distinction here and let the renderer pick based on owner-relationship.

import { PILL_MAX_HEALTH, Terrain } from '@bolo2k/shared';
import { pickTilePosition, readNeighbours } from './tilePicker.js';

export const ART_TILE_SIZE = 16; // size in source PNG (16 px master tile)
const TS = ART_TILE_SIZE;

// --- Mine overlay (single tile cell) -------------------------------------
// [WinBolo gui/tiles.h] MINE_X=19, MINE_Y=3 (units: TILE_SIZE_X/Y).
const MINE_OVERLAY = { sx: 19 * TS, sy: 3 * TS };

// --- Pillbox damage frames -----------------------------------------------
// PILL_GOOD_15 (full armour) → row 7 col 15; descending across row 7.
// PILL_EVIL_15 → row 1 col 9 (the one off-row sprite WinBolo packed into the
// road strip), with PILL_EVIL_14..PILL_EVIL_0 at row 5 cols 14..0.
// Full WinBolo `tilenum.h` mapping; armour clamped to 0..PILL_MAX_HEALTH.
function pillGoodPos(armour: number): { sx: number; sy: number } {
  const clamped = Math.max(0, Math.min(PILL_MAX_HEALTH, armour));
  // PILL_GOOD_n at (n * TS, 7 * TS) for n in 0..15.
  return { sx: clamped * TS, sy: 7 * TS };
}
function pillEvilPos(armour: number): { sx: number; sy: number } {
  const clamped = Math.max(0, Math.min(PILL_MAX_HEALTH, armour));
  if (clamped === 15) return { sx: 9 * TS, sy: 1 * TS }; // PILL_EVIL_15 special-case
  return { sx: clamped * TS, sy: 5 * TS }; // PILL_EVIL_14..0 across row 5
}

// --- Status-grid icons (for the middle-column pillbox/base status grids) -
// 12×12 sprites at the right-hand side of tiles.png. [WinBolo gui/tiles.h
// lines 760-784]. The middle column shows 16 of these per grid, colour-
// coded by alliance state. Sprite coordinates are absolute pixels in the
// tile sheet (not multiples of TS).
export const STATUS_ICON_SIZE = 12;
export const STATUS_SPRITE_COORDS = {
  // Shared dead/destroyed icon (same sprite for pills + bases).
  dead: { sx: 440, sy: 60 },
  // Pillbox alliance states.
  pillGood:    { sx: 440, sy: 48 }, // your pill
  pillAllie:   { sx: 452, sy: 48 },
  pillNeutral: { sx: 428, sy: 66 },
  pillEvil:    { sx: 428, sy: 78 },
  // Pillbox carried-by-tank variants (WinBolo `pillTankGood/Allie/Evil`,
  // pillbox.c:245-257). Used when a pillbox is `inTank == TRUE` — picked up
  // off the map but not yet replaced. Coords from gui/tiles.h:777-784.
  pillTankGood:  { sx: 440, sy: 72 },
  pillTankAllie: { sx: 272, sy: 148 },
  pillTankEvil:  { sx: 440, sy: 84 },
  // Base alliance states.
  baseGood:    { sx: 440, sy: 48 },
  baseAllie:   { sx: 452, sy: 48 },
  baseNeutral: { sx: 428, sy: 54 },
  baseEvil:    { sx: 452, sy: 60 },
} as const;
export type StatusIconKind = keyof typeof STATUS_SPRITE_COORDS;

// --- Base sprites (no damage stages — armour drives a separate HUD bar) --
// [WinBolo gui/tiles.h] BASE_GOOD/NEUTRAL/EVIL coords.
const BASE_GOOD_POS    = { sx: 10 * TS, sy: 1 * TS };
const BASE_NEUTRAL_POS = { sx: 16 * TS, sy: 4 * TS };
const BASE_EVIL_POS    = { sx: 112,     sy: 144 };

// --- Shell projectile sprites (16 directions, small 3-4 px sprites) -----
// [WinBolo gui/tiles.h] SHELL_n_X/Y/WIDTH/HEIGHT for n in 0..15.
const SHELL_FRAMES: ReadonlyArray<{ sx: number; sy: number; w: number; h: number }> = [
  { sx: 452, sy: 72, w: 3, h: 4 }, // SHELL_0  (north)
  { sx: 455, sy: 72, w: 3, h: 4 },
  { sx: 458, sy: 72, w: 4, h: 4 },
  { sx: 452, sy: 76, w: 4, h: 3 },
  { sx: 456, sy: 76, w: 4, h: 3 },
  { sx: 460, sy: 76, w: 4, h: 3 },
  { sx: 452, sy: 79, w: 4, h: 4 }, // SHELL_6 (east)
  { sx: 456, sy: 79, w: 3, h: 4 },
  { sx: 459, sy: 79, w: 3, h: 4 }, // SHELL_8 (south)
  { sx: 452, sy: 83, w: 3, h: 4 },
  { sx: 456, sy: 83, w: 3, h: 4 },
  { sx: 459, sy: 83, w: 4, h: 3 },
  { sx: 452, sy: 87, w: 4, h: 3 }, // SHELL_12 (west)
  { sx: 456, sy: 87, w: 4, h: 3 },
  { sx: 452, sy: 90, w: 4, h: 4 },
  { sx: 456, sy: 90, w: 4, h: 3 },
];

// --- LGM (Little Green Man) frames ---------------------------------------
// [WinBolo gui/tiles.h] LGM0/1/2 + LGM_HELICOPTER. Tiny 3×4 sprites packed
// onto row 5/6 boundary; helicopter is a full 16×16 cell.
const LGM_FRAMES: ReadonlyArray<{ sx: number; sy: number; w: number; h: number }> = [
  { sx: 431, sy: 90, w: 3, h: 4 }, // LGM0
  { sx: 428, sy: 90, w: 3, h: 4 }, // LGM1
  { sx: 434, sy: 90, w: 3, h: 4 }, // LGM2
];
const LGM_HELICOPTER_POS = { sx: 16 * TS, sy: 7 * TS };

// --- Explosion frames (1..8) ---------------------------------------------
// [WinBolo gui/tiles.h] EXPLOSION1..8 sprite cells. Frames 1-6 are at the
// far-right of rows 3-5; frames 7-8 are mid-sheet at row 4 (the "big" ones
// WinBolo uses for TH_KILL_BIG). Frame index 0 → smallest start, 7 → peak.
const EXPLOSION_FRAMES: ReadonlyArray<{ sx: number; sy: number }> = [
  { sx: 29 * TS, sy: 3 * TS }, // EXPLOSION1
  { sx: 30 * TS, sy: 3 * TS }, // EXPLOSION2
  { sx: 29 * TS, sy: 4 * TS }, // EXPLOSION3
  { sx: 30 * TS, sy: 4 * TS }, // EXPLOSION4
  { sx: 29 * TS, sy: 5 * TS }, // EXPLOSION5
  { sx: 30 * TS, sy: 5 * TS }, // EXPLOSION6
  { sx: 18 * TS, sy: 4 * TS }, // EXPLOSION7 (big)
  { sx: 19 * TS, sy: 4 * TS }, // EXPLOSION8 (big)
];

// --- Gunsight cursor -----------------------------------------------------
// [WinBolo gui/tiles.h] GUNSIGHT_X=17, GUNSIGHT_Y=4 (units: TS).
const GUNSIGHT_POS = { sx: 17 * TS, sy: 4 * TS };

// Tank sprite layout: tanks.png is 256×272 at 16-px cell size, so 16 cols ×
// 17 rows. Columns = 16 rotation steps (tank.dir / 16). Rows = team-color
// variants. We use row = (owner % rows).
const TANK_COLS = 16;
const TANK_ROWS = 17;

// Boat sprite layout: boats.png is 256×272 same as tanks.
const BOAT_COLS = 16;
const BOAT_ROWS = 17;

// Maps tank.dir (0..255, 0=N) to a sprite column. The original sheet is
// laid out clockwise starting from the column that points the tank up the
// screen — which corresponds to dir=0=North. Round (not floor) so the
// chosen column matches dirVector's snapping in @bolo2k/sim — keeping
// movement direction aligned with sprite heading.
function dirToCol(dir: number, cols: number): number {
  const wrapped = ((dir % 256) + 256) % 256;
  return Math.round((wrapped / 256) * cols) % cols;
}

export interface LoadedSprites {
  tiles: HTMLImageElement;
  tanks: HTMLImageElement;
  boats: HTMLImageElement;
  // Per-tile overlay canvases keyed by sprite-sheet "sx,sy" — drawTile
  // composites the matching overlay on top of the base tile to soften the
  // per-pixel contrast that strobes under the GPU bilinear sub-pixel transform
  // (renderer.ts `draw()`). Two flavours:
  //   1. "dots on black" tiles (grass, swamp, forest variants) get a 25%-alpha
  //      halo of each dot's colour painted in its 4-neighbours (buildTileHalo).
  //   2. "two-colour checker" tiles (BUILD_SOLID) get each pixel overlaid with
  //      25%-alpha of the OTHER colour (buildCheckerDamper) so the pair sits
  //      closer to the mid-tone — less swing for the bilinear smear to chew.
  // Overlays live in small per-tile 16×16 canvases (one per haloed position)
  // so the main tiles atlas stays an Image — mixing a canvas-source into the
  // main blit path produced a grass/forest regression in earlier testing.
  tileHalos: ReadonlyMap<string, HTMLCanvasElement>;
}

let cached: LoadedSprites | null = null;
let loadPromise: Promise<LoadedSprites> | null = null;

function load(name: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load /sprites/${name}.png`));
    img.src = `/sprites/${name}.png`;
  });
}

// Read a 16×16 region of the tiles atlas into an RGBA Uint8ClampedArray.
// Shared helper for the two overlay builders below.
function readTilePixels(
  tilesImg: HTMLImageElement,
  srcX: number,
  srcY: number,
): Uint8ClampedArray {
  const scratch = document.createElement('canvas');
  scratch.width = 16;
  scratch.height = 16;
  const sctx = scratch.getContext('2d');
  if (!sctx) return new Uint8ClampedArray(16 * 16 * 4);
  sctx.drawImage(tilesImg, srcX, srcY, 16, 16, 0, 0, 16, 16);
  return sctx.getImageData(0, 0, 16, 16).data;
}

// Build a 16×16 transparent canvas with quarter-alpha halo pixels surrounding
// every coloured dot in a `(srcX, srcY)`-positioned 16×16 tile from the
// tiles atlas. Targets the "isolated dots on pure black" pattern shared by
// swamp (cyan + dark-green dots), grass (dark-green dots only), and the
// 10 forest variants (dark + bright green dots). Under the sub-pixel CSS
// transform (renderer.ts `draw()`), the GPU compositor's bilinear
// interpolation smears each isolated bright pixel into the black surround
// across frames, which reads as strobing during fast scroll. Quarter-alpha
// neighbours give the bilinear smear something less contrasty to blend
// into. Dot positions themselves are skipped so the sprite's silhouette
// stays intact.
function buildTileHalo(
  tilesImg: HTMLImageElement,
  srcX: number,
  srcY: number,
): HTMLCanvasElement {
  const halo = document.createElement('canvas');
  halo.width = 16;
  halo.height = 16;
  const haloCtx = halo.getContext('2d');
  if (!haloCtx) return halo;
  const src = readTilePixels(tilesImg, srcX, srcY);
  const haloImg = haloCtx.createImageData(16, 16);
  const dst = haloImg.data;
  const isDot = (i: number) => src[i] !== 0 || src[i + 1] !== 0 || src[i + 2] !== 0;
  const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const i = (y * 16 + x) * 4;
      if (!isDot(i)) continue;
      for (const [dx, dy] of NEIGHBORS) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || nx >= 16 || ny < 0 || ny >= 16) continue;
        const ni = (ny * 16 + nx) * 4;
        if (isDot(ni)) continue;          // never tint a dot
        if (dst[ni + 3] !== 0) continue;  // already haloed by another dot
        dst[ni] = src[i]!;
        dst[ni + 1] = src[i + 1]!;
        dst[ni + 2] = src[i + 2]!;
        dst[ni + 3] = 64;                 // 25% alpha
      }
    }
  }
  haloCtx.putImageData(haloImg, 0, 0);
  return halo;
}

// Build a 16×16 overlay that softens the BUILD_SOLID checker. Every pixel of
// BUILD_SOLID is either dark-brown (64,32,32) or light-brown (192,160,111) in
// a perfect alternating grid — every pixel boundary is a sharp contrast step
// (~197 distance), which makes the GPU bilinear smear during the sub-pixel
// CSS transform very visible (the checker flickers between sharp and a
// half-blended grey). The dots-halo trick doesn't fit here (no black
// neighbours), so we use a different shape: at each pixel we composite 25 %
// alpha of the OPPOSITE checker colour. After alpha blending, each dark pixel
// sits ~25 % closer to the light colour and vice versa — the per-pixel
// contrast drops from ~197 to ~99, and the bilinear smear has roughly half
// the swing to chew on. The checker remains visible at rest.
function buildCheckerDamper(
  tilesImg: HTMLImageElement,
  srcX: number,
  srcY: number,
): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = 16;
  out.height = 16;
  const outCtx = out.getContext('2d');
  if (!outCtx) return out;
  const src = readTilePixels(tilesImg, srcX, srcY);
  // Tally the two dominant colours.
  const counts = new Map<string, { r: number; g: number; b: number; n: number }>();
  for (let i = 0; i < src.length; i += 4) {
    const key = `${src[i]},${src[i + 1]},${src[i + 2]}`;
    const e = counts.get(key);
    if (e) e.n++;
    else counts.set(key, { r: src[i]!, g: src[i + 1]!, b: src[i + 2]!, n: 1 });
  }
  const sorted = [...counts.values()].sort((a, b) => b.n - a.n);
  if (sorted.length < 2) return out;
  const A = sorted[0]!;
  const B = sorted[1]!;
  const outImg = outCtx.createImageData(16, 16);
  const dst = outImg.data;
  for (let i = 0; i < src.length; i += 4) {
    const r = src[i]!;
    const g = src[i + 1]!;
    const b = src[i + 2]!;
    // Overlay each pixel with the OTHER dominant colour at 25 % alpha.
    if (r === A.r && g === A.g && b === A.b) {
      dst[i] = B.r;
      dst[i + 1] = B.g;
      dst[i + 2] = B.b;
      dst[i + 3] = 64;
    } else if (r === B.r && g === B.g && b === B.b) {
      dst[i] = A.r;
      dst[i + 1] = A.g;
      dst[i + 2] = A.b;
      dst[i + 3] = 64;
    }
  }
  outCtx.putImageData(outImg, 0, 0);
  return out;
}

export async function loadSprites(): Promise<LoadedSprites> {
  if (cached) return cached;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    const [tiles, tanks, boats] = await Promise.all([load('tiles'), load('tanks'), load('boats')]);
    // Build per-tile overlay canvases keyed by sprite-sheet position. Positions
    // mirror tilePicker.ts so drawTile can look up by the picked `at.sx,at.sy`.
    const tileHalos = new Map<string, HTMLCanvasElement>();
    const dotsTiles: ReadonlyArray<readonly [string, number, number]> = [
      ['GRASS_TILE', 32, 16],
      ['SWAMP_TILE', 112, 16],
      ['FOREST_TILE', 48, 16],
      ['FOREST_SINGLE', 128, 144],
      ['FOREST_BR', 144, 144],
      ['FOREST_BL', 160, 144],
      ['FOREST_AL', 176, 144],
      ['FOREST_AR', 192, 144],
      ['FOREST_RIGHT', 208, 144],
      ['FOREST_LEFT', 224, 144],
      ['FOREST_BELOW', 240, 144],
      ['FOREST_ABOVE', 256, 144],
    ];
    for (const [, sx, sy] of dotsTiles) {
      tileHalos.set(`${sx},${sy}`, buildTileHalo(tiles, sx, sy));
    }
    // BUILD_SOLID checker damper.
    tileHalos.set('272,16', buildCheckerDamper(tiles, 272, 16));
    cached = { tiles, tanks, boats, tileHalos };
    return cached;
  })();
  return loadPromise;
}

export function getSprites(): LoadedSprites | null {
  return cached;
}

export function drawTile(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  terrain: Uint8Array,
  tileX: number,
  tileY: number,
  terrainCode: number,
  dx: number,
  dy: number,
  size: number,
  hasMine: boolean,
): void {
  const neighbours = readNeighbours(terrain, tileX, tileY);
  const at = pickTilePosition(terrainCode, neighbours);
  ctx.drawImage(sprites.tiles, at.sx, at.sy, ART_TILE_SIZE, ART_TILE_SIZE, dx, dy, size, size);
  // Position-keyed overlay (if any) — dots-halo for grass/swamp/forest, or
  // checker-damper for BUILD_SOLID. See LoadedSprites.tileHalos.
  const overlay = sprites.tileHalos.get(`${at.sx},${at.sy}`);
  if (overlay) {
    ctx.drawImage(overlay, 0, 0, ART_TILE_SIZE, ART_TILE_SIZE, dx, dy, size, size);
  }
  if (hasMine) {
    // Subtle corner overlay to indicate a (visible) mine.
    ctx.drawImage(
      sprites.tiles,
      MINE_OVERLAY.sx,
      MINE_OVERLAY.sy,
      ART_TILE_SIZE,
      ART_TILE_SIZE,
      dx,
      dy,
      size,
      size,
    );
  }
  void Terrain; // referenced only for documentation in tilePicker
}

export function drawTank(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  owner: number,
  dir: number,
  cx: number,
  cy: number,
  size: number,
): void {
  const col = dirToCol(dir, TANK_COLS);
  const row = ((owner % TANK_ROWS) + TANK_ROWS) % TANK_ROWS;
  ctx.drawImage(
    sprites.tanks,
    col * ART_TILE_SIZE,
    row * ART_TILE_SIZE,
    ART_TILE_SIZE,
    ART_TILE_SIZE,
    cx - size / 2,
    cy - size / 2,
    size,
    size,
  );
}

export function drawBoat(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  ownerOfRider: number,
  dir: number,
  cx: number,
  cy: number,
  size: number,
): void {
  const col = dirToCol(dir, BOAT_COLS);
  // Use the rider's team-colour row so allies / enemies are visually distinct
  // even while a tank is mid-ferry.
  const row = ((ownerOfRider % BOAT_ROWS) + BOAT_ROWS) % BOAT_ROWS;
  ctx.drawImage(
    sprites.boats,
    col * ART_TILE_SIZE,
    row * ART_TILE_SIZE,
    ART_TILE_SIZE,
    ART_TILE_SIZE,
    cx - size / 2,
    cy - size / 2,
    size,
    size,
  );
}

// Pick the damage-stage pillbox sprite from tiles.png. Armour 15 = intact, 0
// = destroyed (the empty shell that's pickup-able by a tank that drives over
// it). `kind` selects between the green/blue "good" frame bank and the red
// "evil" bank. Neutral pills attack everyone, so the renderer maps them to
// 'evil' from the local player's POV.
export type PillKind = 'good' | 'evil';
export function drawPillboxSprite(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  armour: number,
  kind: PillKind,
  cx: number,
  cy: number,
  size: number,
): void {
  const { sx, sy } = kind === 'good' ? pillGoodPos(armour) : pillEvilPos(armour);
  ctx.drawImage(sprites.tiles, sx, sy, TS, TS, cx - size / 2, cy - size / 2, size, size);
}

// Base sprite — 3-way variant by owner relationship. WinBolo's base sheet
// distinguishes friendly / neutral / enemy (no damage frames; armour is
// communicated via the HUD bar).
export type BaseKind = 'good' | 'neutral' | 'evil';
export function drawBaseSprite(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  kind: BaseKind,
  cx: number,
  cy: number,
  size: number,
): void {
  const pos = kind === 'good' ? BASE_GOOD_POS : kind === 'neutral' ? BASE_NEUTRAL_POS : BASE_EVIL_POS;
  ctx.drawImage(sprites.tiles, pos.sx, pos.sy, TS, TS, cx - size / 2, cy - size / 2, size, size);
}

// Shell projectile — 16-direction sprite. dir is the same 0..255 brad units
// the tanks/boats use. WinBolo's shell sprites are 3-4 px each; we render at
// the same physical scale as a tile so they read at distance.
export function drawShellSprite(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  dir: number,
  cx: number,
  cy: number,
  tilePx: number,
): void {
  const idx = dirToCol(dir, SHELL_FRAMES.length);
  const f = SHELL_FRAMES[idx]!;
  // Render so the shell occupies roughly a 6-px footprint within a tile cell —
  // big enough to read while moving, small enough to stay obviously a shell.
  const scale = (tilePx * 0.45) / Math.max(f.w, f.h);
  const dw = f.w * scale;
  const dh = f.h * scale;
  ctx.drawImage(sprites.tiles, f.sx, f.sy, f.w, f.h, cx - dw / 2, cy - dh / 2, dw, dh);
}

// LGM walking sprite. Three frames; cycle on a coarse tick to suggest motion.
export function drawLgmSprite(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  frame: number,
  cx: number,
  cy: number,
  tilePx: number,
): void {
  const f = LGM_FRAMES[((frame % LGM_FRAMES.length) + LGM_FRAMES.length) % LGM_FRAMES.length]!;
  const scale = (tilePx * 0.55) / Math.max(f.w, f.h);
  const dw = f.w * scale;
  const dh = f.h * scale;
  ctx.drawImage(sprites.tiles, f.sx, f.sy, f.w, f.h, cx - dw / 2, cy - dh / 2, dw, dh);
}

// LGM-drop helicopter (parachute incoming). Single 16×16 cell.
export function drawHelicopterSprite(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  cx: number,
  cy: number,
  size: number,
): void {
  ctx.drawImage(
    sprites.tiles,
    LGM_HELICOPTER_POS.sx,
    LGM_HELICOPTER_POS.sy,
    TS,
    TS,
    cx - size / 2,
    cy - size / 2,
    size,
    size,
  );
}

// Explosion frame — `progress` runs 0..1 across the explosion's lifetime; we
// pick frame 0..7 from the EXPLOSION1..8 cells. `big` selects whether to use
// the larger frames 7-8 at peak (true) or stay within the small cells 1-6
// (false). Matches WinBolo's TH_KILL_BIG vs TH_KILL_SMALL distinction.
export function drawExplosionSprite(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  progress: number,
  big: boolean,
  cx: number,
  cy: number,
  size: number,
): void {
  const clamped = Math.max(0, Math.min(0.9999, progress));
  const total = big ? 8 : 6;
  const frame = Math.floor(clamped * total);
  const pos = EXPLOSION_FRAMES[frame]!;
  const draw = big ? size * 1.4 : size; // big explosions visibly larger
  ctx.drawImage(sprites.tiles, pos.sx, pos.sy, TS, TS, cx - draw / 2, cy - draw / 2, draw, draw);
}

// Pillbox-status grid icon at (dx,dy), scaled to `size` × `size`. Used by
// MiddleColumn to render each of the 16 pill/base cells. The source sprite
// is 12×12; scale up via the renderer to fit the panel.
export function drawStatusIcon(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  kind: StatusIconKind,
  dx: number,
  dy: number,
  size: number,
): void {
  const pos = STATUS_SPRITE_COORDS[kind];
  ctx.drawImage(sprites.tiles, pos.sx, pos.sy, STATUS_ICON_SIZE, STATUS_ICON_SIZE, dx, dy, size, size);
}

// Centre-of-grid indicator. WinBolo uses the full 16×16 PILL_GOOD15 sprite
// for the pills grid and the BASE_GOOD sprite for the bases grid (see
// gui/linux/draw.c drawSetBasesStatusClear / drawSetPillsStatusClear).
export function drawStatusIndicator(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  kind: 'pill' | 'base',
  dx: number,
  dy: number,
  size: number,
): void {
  const pos = kind === 'pill' ? pillGoodPos(15) : BASE_GOOD_POS;
  ctx.drawImage(sprites.tiles, pos.sx, pos.sy, TS, TS, dx, dy, size, size);
}

// Gunsight cursor — single 16×16 cell. Drawn at the local tank's aim point.
export function drawGunsightSprite(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  cx: number,
  cy: number,
  size: number,
): void {
  ctx.drawImage(
    sprites.tiles,
    GUNSIGHT_POS.sx,
    GUNSIGHT_POS.sy,
    TS,
    TS,
    cx - size / 2,
    cy - size / 2,
    size,
    size,
  );
}
